export interface QuestionnaireExclusiveGroup {
  selected: string[];
  disable: string[];
}

export const QUESTIONNAIRE_EXCLUSIVE_GROUPS: QuestionnaireExclusiveGroup[] = [
  {
    selected: ['OSDIAndSPEED'],
    disable: ['OSDI', 'OSDI6', 'SPEED', 'SPEEDII']
  },
  { selected: ['OSDI', 'SPEED'], disable: ['OSDIAndSPEED'] },
  { selected: ['SPEEDII'], disable: ['SPEED'] },
  { selected: ['SPEED'], disable: ['SPEEDII'] },
  { selected: ['OSDI'], disable: ['OSDI6'] },
  { selected: ['OSDI6'], disable: ['OSDI', 'DEWSIII_Q'] },
  { selected: ['DERFS'], disable: ['CDERFS'] },
  { selected: ['CDERFS'], disable: ['DERFS'] },
  { selected: ['DEWSIII_Q'], disable: ['OSDI6'] }
];

export const INTAKE_STANDARD_KEY = 'standard';

export const SCORED_QUESTIONNAIRE_IDS_DISABLED_BY_INTAKE = [
  'OSDI',
  'OSDI6',
  'SPEED',
  'SPEEDII',
  'OSDIAndSPEED'
];

export const SYMPTOM_SCORE_QUESTIONNAIRE_IDS = [
  'DEQ',
  'OSDI',
  'OSDI6',
  'SPEED',
  'SPEEDII',
  'OSDIAndSPEED',
  'DEWSIII_Q'
];

export const RISK_AND_HISTORY_QUESTIONNAIRE_IDS = [
  'DERFS',
  'CDERFS',
  'FITZPATRICK',
  'MEDICATION'
];

export const COMPATIBILITY_TOGGLE_QUESTIONNAIRE_IDS = [
  'OSDI',
  'OSDI6',
  'SPEED',
  'OSDIAndSPEED',
  'SPEEDII',
  'DERFS',
  'CDERFS',
  'DEWSIII_Q'
];

export type QuestionnaireSelectionCategoryId =
  | 'symptomScores'
  | 'riskAndHistory'
  | 'intakeForms'
  | 'customQuestionnaires'
  | 'consentForms';

export interface QuestionnaireSelectionCategory {
  id: QuestionnaireSelectionCategoryId;
  label: string;
  formType: string;
  itemKeyToNamePairs: { [key: string]: string };
  subscriptionPlanAccessControl: string;
}

export function selectedIdMap(selectedIds: string[]): { [id: string]: boolean } {
  const map: { [id: string]: boolean } = {};
  selectedIds.forEach(id => {
    if (id) {
      map[id] = true;
    }
  });
  return map;
}

export function disabledQuestionnaireIdsForSelection(
  selectedIds: string[],
  intakeFormsEnabled: boolean
): string[] {
  const selected = selectedIdMap(selectedIds);
  const disabled: { [id: string]: boolean } = {};

  QUESTIONNAIRE_EXCLUSIVE_GROUPS.forEach(group => {
    const groupTriggered = group.selected.some(id => !!selected[id]);
    if (groupTriggered) {
      group.disable.forEach(id => {
        disabled[id] = true;
      });
    }
  });

  if (intakeFormsEnabled && selected[INTAKE_STANDARD_KEY]) {
    SCORED_QUESTIONNAIRE_IDS_DISABLED_BY_INTAKE.forEach(id => {
      disabled[id] = true;
    });
  }

  return Object.keys(disabled);
}

export function areQuestionnaireSelectionsCompatible(
  selectedIds: string[],
  intakeFormsEnabled: boolean
): boolean {
  const selected = selectedIdMap(selectedIds);
  const disabledIds = disabledQuestionnaireIdsForSelection(selectedIds, intakeFormsEnabled);
  return disabledIds.every(id => !selected[id]);
}

export function resolveCompatibleQuestionnaireSelection(
  selectedIds: string[],
  intakeFormsEnabled: boolean,
  preferredIds?: string[]
): string[] {
  const remaining = uniqueStrings(selectedIds);
  const preferred = selectedIdMap(preferredIds || []);
  while (
    remaining.length > 0 &&
    !areQuestionnaireSelectionsCompatible(remaining, intakeFormsEnabled)
  ) {
    const disabledIds = disabledQuestionnaireIdsForSelection(remaining, intakeFormsEnabled);
    const disabled = selectedIdMap(disabledIds);
    let dropIndex = -1;
    for (let index = remaining.length - 1; index >= 0; index--) {
      if (disabled[remaining[index]] && !preferred[remaining[index]]) {
        dropIndex = index;
        break;
      }
    }
    if (dropIndex === -1) {
      for (let index = remaining.length - 1; index >= 0; index--) {
        if (disabled[remaining[index]]) {
          dropIndex = index;
          break;
        }
      }
    }
    if (dropIndex === -1) {
      break;
    }
    remaining.splice(dropIndex, 1);
  }
  return remaining;
}

export function areAllIdsSelected(selectedIds: string[], requiredIds: string[]): boolean {
  if (!requiredIds || requiredIds.length === 0) {
    return false;
  }
  const selected = selectedIdMap(selectedIds);
  return requiredIds.every(id => !!selected[id]);
}

/**
 * True only when the selection is exactly `requiredIds` - no extras, nothing missing.
 * Used for template chip highlighting: `areAllIdsSelected` is a subset test, so nested
 * or duplicate templates would all light up at once.
 */
export function isExactIdMatch(selectedIds: string[], requiredIds: string[]): boolean {
  const required = uniqueStrings(requiredIds || []);
  const selected = uniqueStrings(selectedIds || []);
  if (required.length === 0 || required.length !== selected.length) {
    return false;
  }
  return areAllIdsSelected(selected, required);
}

export function isMultiSendRestrictionActive(
  selectedIds: string[],
  intakeFormsEnabled: boolean
): boolean {
  return disabledQuestionnaireIdsForSelection(selectedIds, intakeFormsEnabled).length > 0;
}

export function buildKeyToNameSubset(
  allPairs: { [key: string]: string },
  keys: string[]
): { [key: string]: string } {
  const subset: { [key: string]: string } = {};
  keys.forEach(key => {
    if (allPairs && allPairs[key]) {
      subset[key] = allPairs[key];
    }
  });
  return subset;
}

export function uniqueStrings(values: string[]): string[] {
  const seen: { [key: string]: boolean } = {};
  const unique: string[] = [];
  values.forEach(value => {
    if (value && !seen[value]) {
      seen[value] = true;
      unique.push(value);
    }
  });
  return unique;
}

export function selectedIdsFromValues(values: { [key: string]: boolean }): string[] {
  return Object.keys(values || {}).filter(key => values[key] === true);
}

export function hasNamedItems(itemKeyToNamePairs: { [key: string]: string }): boolean {
  return !!(itemKeyToNamePairs && Object.keys(itemKeyToNamePairs).length > 0);
}

export function buildQuestionnaireSelectionCategories(options: {
  standardKeyToName: { [key: string]: string };
  customKeyToName: { [key: string]: string };
  consentKeyToName: { [key: string]: string };
  intakeKeyToName: { [key: string]: string };
  includeIntake: boolean;
}): QuestionnaireSelectionCategory[] {
  const categories: QuestionnaireSelectionCategory[] = [];
  const symptomScores = buildKeyToNameSubset(
    options.standardKeyToName,
    SYMPTOM_SCORE_QUESTIONNAIRE_IDS
  );
  if (hasNamedItems(symptomScores)) {
    categories.push({
      id: 'symptomScores',
      label: 'Symptom Scores',
      formType: 'questionnaires',
      itemKeyToNamePairs: symptomScores,
      subscriptionPlanAccessControl: ''
    });
  }

  const riskAndHistory = buildKeyToNameSubset(
    options.standardKeyToName,
    RISK_AND_HISTORY_QUESTIONNAIRE_IDS
  );
  if (hasNamedItems(riskAndHistory)) {
    categories.push({
      id: 'riskAndHistory',
      label: 'Risk & History',
      formType: 'questionnaires',
      itemKeyToNamePairs: riskAndHistory,
      subscriptionPlanAccessControl: ''
    });
  }

  if (options.includeIntake && hasNamedItems(options.intakeKeyToName)) {
    categories.push({
      id: 'intakeForms',
      label: 'Intake Form(s)',
      formType: 'intakeForms',
      itemKeyToNamePairs: options.intakeKeyToName,
      subscriptionPlanAccessControl: ''
    });
  }

  if (hasNamedItems(options.customKeyToName)) {
    categories.push({
      id: 'customQuestionnaires',
      label: 'Custom Questionnaire(s)',
      formType: '',
      itemKeyToNamePairs: options.customKeyToName,
      subscriptionPlanAccessControl: 'questionnaires.CUSTOM'
    });
  }

  if (hasNamedItems(options.consentKeyToName)) {
    categories.push({
      id: 'consentForms',
      label: 'Consent Form(s)',
      formType: 'consentForms',
      itemKeyToNamePairs: options.consentKeyToName,
      subscriptionPlanAccessControl: ''
    });
  }

  return categories;
}
